import { ReactNode, useState } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/shared/lib/utils";

interface CodeBlockProps {
  code: string;
  title?: string;
  className?: string;
}

const tokenPattern =
  /("(?:[^"\\]|\\.)*")(?=\s*:)|("(?:[^"\\]|\\.)*")|(\$[A-Za-z_][A-Za-z0-9_]*)|(\b\d+(?:\.\d+)?\b)/g;

function highlight(code: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let last = 0;
  let index = 0;
  for (const match of code.matchAll(tokenPattern)) {
    const start = match.index ?? 0;
    if (start > last) {
      nodes.push(code.slice(last, start));
    }
    const [text, jsonKey, string, variable] = match;
    const className = jsonKey
      ? "text-[#D8DEE9]"
      : string
        ? "text-[#A3BE8C]"
        : variable
          ? "text-[#81A1C1]"
          : "text-[#D08770]";
    nodes.push(
      <span key={index} className={className}>
        {text}
      </span>
    );
    last = start + text.length;
    index += 1;
  }
  if (last < code.length) {
    nodes.push(code.slice(last));
  }
  return nodes;
}

export default function CodeBlock({ code, title, className }: CodeBlockProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className={cn(
        "rounded-lg border border-[#232830] bg-[#2B303B] overflow-hidden",
        className
      )}
    >
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#232830] bg-[#333A46]">
        <span className="text-xs font-medium text-[#A7ADBA]">
          {title ?? "Example"}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          aria-label="Copy code"
          className="text-[#A7ADBA] hover:text-white transition-colors"
        >
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto font-mono text-sm leading-relaxed text-[#A7ADBA]">
        <code>{highlight(code)}</code>
      </pre>
    </div>
  );
}
